import { useState } from 'react';
import type { TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { median, avgArr } from '../../utils/format';
import { ALL_DATA } from '../../data/deals';
import { MetricCard } from '../ui/MetricCard';
import { SectionHeader } from '../ui/SectionHeader';
interface Props {
  state: TabState;
}
function SCMatrix({
  region,
  sub,
  cqState
}: {
  region: string;
  sub: string;
  cqState: TabState;
}) {
  const ipData = ALL_DATA.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    if (!scMap[d.sc]) scMap[d.sc] = {};
    const bkts = bucketProducts(d.products || '');
    for (const bkt of bkts) {
      const uniq = d.sc + '|' + bkt + '|' + d.id;
      if (seenDR[uniq]) continue;
      seenDR[uniq] = 1;
      scMap[d.sc][bkt] = (scMap[d.sc][bkt] || 0) + 1;
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-74">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-75">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-76">
        <tr data-id="element-77">
          <th data-id="element-78">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-79">{c}</th>)}
          <th data-id="element-80">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-81">
        {scRows.map(row => <tr key={row.full} data-id="element-82">
            <td title={row.full} data-id="element-83">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-84">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-85">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-86">—</span>}
              </td>)}
            <td data-id="element-87"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-88">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-89">
        <tr data-id="element-90">
          <td data-id="element-91">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-92"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-93">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-94"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-95">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}
export function ODPanel({
  state
}: Props) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const scData = buildSCList(state.r, state.sub, state);
  const allIP = ALL_DATA.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = ALL_DATA.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = allForGnarr.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const ages = allIP.map(d => d.age);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  return <div data-id="element-96">
      {/* Metrics */}
      <div className="mrow" data-id="element-97">
        <MetricCard label="In-progress deals" value={allIP.length} sub={`${scData.length} active SCs`} colorClass="c-blue" data-id="element-98" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-99" />
        <MetricCard label="Median age" value={`${median(ages)}d`} sub={`mean ${avgArr(ages)}d`} colorClass="c-gold" data-id="element-100" />
        <MetricCard label="GNARR Influenced" value={fmtG(oppGnarr)} sub="total opp GNARR (created qtr)" colorClass="c-green" data-id="element-101" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — in-progress deals (click row to expand)" data-id="element-102" />
      <div className="tw" data-id="element-103">
        <table className="tbl" data-id="element-104">
          <thead data-id="element-105">
            <tr data-id="element-106">
              <th style={{
              width: '28%'
            }} data-id="element-107">SC Name</th>
              <th style={{
              width: '22%'
            }} data-id="element-108">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-109">Avg age</th>
              <th style={{
              width: '16%'
            }} data-id="element-110">Median age</th>
              <th style={{
              width: '18%'
            }} data-id="element-111">Book GNARR</th>
            </tr>
          </thead>
          <tbody data-id="element-112">
            {scData.map((p, idx) => <>
                <tr key={p.n} onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} data-id="element-113">
                  <td data-id="element-114">
                    <span className="name-link" data-id="element-115">
                      {p.n}
                      <span className={`chev${expandedIdx === idx ? ' open' : ''}`} data-id="element-116">▾</span>
                    </span>
                  </td>
                  <td data-id="element-117">
                    <div className="pbg" style={{
                  width: 80
                }} data-id="element-118">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-119" />
                    </div>
                    <strong data-id="element-120">{p.ip}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-121">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-122">{p.med}d</td>
                  <td data-id="element-123">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedIdx === idx && <tr key={`exp-${idx}`} data-id="element-124">
                    <td colSpan={5} style={{
                padding: 0
              }} data-id="element-125">
                      <div className="exp-inner" data-id="element-126">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-127">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <div className="deal-grid" data-id="element-128">
                          {p.deals.map(d => <div key={d.id} className="deal-chip" data-id="element-129">
                              <div className="dc-id" data-id="element-130">{d.id}</div>
                              <div className="dc-name" data-id="element-131">{d.account.substring(0, 35)}</div>
                              <div className="dc-tags" data-id="element-132">
                                <span className={`badge ${d.age > 90 ? 'b-red' : d.age > 45 ? 'b-amber' : 'b-gray'}`} data-id="element-133">{d.age}d</span>
                                <span className="badge b-blue" data-id="element-134">{fmtG(d.gnarr)}</span>
                                <span className="badge b-purple" style={{
                          fontSize: 8
                        }} data-id="element-135">{d.close_q}</span>
                                {d.age > 90 && <span className="badge b-red" data-id="element-136">⚠ aged</span>}
                              </div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-137">
                                {d.sub_region} · {d.offering.substring(0, 26)}
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </td>
                  </tr>}
              </>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-138" />
      <div className="tw" data-id="element-139">
        <div className="sub" data-id="element-140">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-141">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-142">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data-id="element-143" />
          </table>
        </div>
      </div>
    </div>;
}